import Node from '../node.js';
import { PARAM_TYPE, PARAM_UNITS } from '../connector.js';
import Utils3D from '../../utils/utils3D.js';
import Utils from '../utils.js';
import Spline from '../spline.js';
import GeoUtils from '../geoUtils.js';

export default class NodeFit extends Node
{
	constructor(pParent)
	{
		super(pParent);

		this.addInput(PARAM_TYPE.GEOMETRY, 'geo', null);

		this.addInput(PARAM_TYPE.FLOAT, 'width', 1, { units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'height', 1, { units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'depth', 1, { units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.BOOLEAN, 'fit_width', true);
		this.addInput(PARAM_TYPE.BOOLEAN, 'fit_height', true);
		this.addInput(PARAM_TYPE.BOOLEAN, 'fit_depth', true);

		this.addInput(PARAM_TYPE.GEOMETRY, 'target', null);

		this.addInput(PARAM_TYPE.BOOLEAN, 'intelligent_width_scaling', false);
		this.addInput(PARAM_TYPE.BOOLEAN, 'intelligent_height_scaling', false);
		this.addInput(PARAM_TYPE.BOOLEAN, 'intelligent_depth_scaling', false);

		this.addInput(PARAM_TYPE.BOOLEAN, 'scale_in_world_space', false);

		this.addOutput(PARAM_TYPE.GEOMETRY, 'geo');

		this.isLastResult3DCurve = false;
	}

	getType()
	{
		return 'fit';
	}

	getIsInputEnabledSubclass(strInputName)
	{
		let bHaveConnectedNode = this.getInput('target').getConnectedNode();
		if(['width', 'height'].indexOf(strInputName) >= 0)
			return !bHaveConnectedNode;

		// See if have a curve or a set of meshes connected
		let bAllowCurveControls = false;
		const lastResult = this.getLastInputResult('geo');
		if(lastResult && !Utils.getIsMeshObject(lastResult[0]))
			bAllowCurveControls = true;
		if(!bAllowCurveControls)
		{
			let pInputNode = this.getInputNode('geo');
			if(pInputNode)
			{
				const strType = pInputNode.getType();
				if(strType == 'loadCurve' || strType == 'createCurve')
					bAllowCurveControls = true;
			}
		}

		if(['intelligent_depth_scaling'].indexOf(strInputName) >= 0)
			return !bAllowCurveControls;

		if(['scale_in_world_space'].indexOf(strInputName) >= 0)
			return !bAllowCurveControls;

		if(['depth'].indexOf(strInputName) >= 0)
			return (!bAllowCurveControls && !bHaveConnectedNode) || this.isLastResult3DCurve;

		if(['fit_depth'].indexOf(strInputName) >= 0)
			return !bAllowCurveControls || this.isLastResult3DCurve;

		return true;
	}

	generateSubclass(scene, inputValues, outputName, computedOutputResults)
	{	
		this.isLastResult3DCurve = false;
		let pSourcePrimitivesList = inputValues['geo'];
		if(!pSourcePrimitivesList || pSourcePrimitivesList.length === 0)
			return null;
		
		if(!Array.isArray(pSourcePrimitivesList))
			pSourcePrimitivesList = [pSourcePrimitivesList];

		let pTargetPrimitivesList = inputValues['target'];
		if(pTargetPrimitivesList && !Array.isArray(pTargetPrimitivesList))
			pTargetPrimitivesList = [pTargetPrimitivesList];

		let vecTargetSize;
		if(pTargetPrimitivesList && pTargetPrimitivesList.length > 0)
		{
			// We're aligning using an input mesh; get its boudning box.
			if(Utils.getIsMeshObject(pTargetPrimitivesList[0]))
			{
				let bbox = Utils3D.getObjectsBBox(pTargetPrimitivesList);
				vecTargetSize = bbox.maximumWorld.subtract(bbox.minimumWorld);
			}
			else
			{
				// We're targeting splines
				let listTargetSplines = [];
				pTargetPrimitivesList.forEach((curveData) =>
				{
					listTargetSplines.push(new Spline(curveData));
				});
				let bboxCurves = Spline.getCurvesBBox(listTargetSplines);
				vecTargetSize = bboxCurves.maximum.subtract(bboxCurves.minimum);
			}
		}
		else
		{
			// We're aligning using explicit values
			vecTargetSize = new BABYLON.Vector3(inputValues['width'], inputValues['height'], inputValues['depth']);
		}

		let vecCurrentSize;
		let vecCurrCenter;
		let listSplines = [];
		let bIsHandlingMeshes = true;
		if(Utils.getIsMeshObject(pSourcePrimitivesList[0]))
		{
			let bboxGeos = Utils3D.getObjectsBBox(pSourcePrimitivesList);
			vecCurrentSize = bboxGeos.maximumWorld.subtract(bboxGeos.minimumWorld);
			vecCurrCenter = bboxGeos.maximumWorld.add(bboxGeos.minimumWorld).scale(0.5);
		}
		else
		{
			bIsHandlingMeshes = false;
			pSourcePrimitivesList.forEach((curveData) =>
			{
				listSplines.push(new Spline(curveData));
			});
			let bboxCurves = Spline.getCurvesBBox(listSplines);
			vecCurrentSize = bboxCurves.maximum.subtract(bboxCurves.minimum);
			vecCurrCenter = bboxCurves.maximum.add(bboxCurves.minimum).scale(0.5);
		}

		let vecScales = new BABYLON.Vector3(1, 1, 1);
		if(Math.abs(vecCurrentSize.x) > 1e-5 && inputValues['fit_width'])
			vecScales.x = vecTargetSize.x/vecCurrentSize.x;
		if(Math.abs(vecCurrentSize.y) > 1e-5 && inputValues['fit_height'])
			vecScales.y = vecTargetSize.y/vecCurrentSize.y;
		if(vecCurrentSize.z !== undefined && vecTargetSize.z !== undefined && Math.abs(vecCurrentSize.z) > 1e-5 && inputValues['fit_depth'])
			vecScales.z = vecTargetSize.z/vecCurrentSize.z;

		let pResult;
		if(bIsHandlingMeshes)
		{
			// We can selective scale the mesh in an intelligent or not way.
			const nlrsScalingFlags = [inputValues['intelligent_width_scaling'], inputValues['intelligent_height_scaling'], inputValues['intelligent_depth_scaling']];

			// First, apply naive scaling:
			for(let pSrcMesh of pSourcePrimitivesList)
			{
				pSrcMesh.position = pSrcMesh.position.subtract(vecCurrCenter);

				GeoUtils.scaleMesh(pSrcMesh, vecScales.x, vecScales.y, vecScales.z, inputValues['scale_in_world_space'], nlrsScalingFlags);

				pSrcMesh.makeGeometryUnique();
				pSrcMesh.bakeCurrentTransformIntoVertices();
				pSrcMesh.position = pSrcMesh.position.add(vecCurrCenter);

			}

			pResult = inputValues['geo'];
		}
		else
		{
			for(let pSrcSpline of listSplines)
			{
				const bIs3D = pSrcSpline.getIs3D();
				this.isLastResult3DCurve ||= bIs3D;
				if(bIs3D)
					pSrcSpline.scale3D(vecScales, vecCurrCenter);
				else
					pSrcSpline.scale(vecScales, vecCurrCenter, inputValues['intelligent_width_scaling'], inputValues['intelligent_height_scaling']);
			}

			pResult = listSplines[0].saveToObject();
		}

		return pResult;
	}
}